import React, { useEffect, useState, useContext, useRef, memo, useCallback, useMemo } from "react";
import { Table, TableHead, TableRow, TableCell, TableContainer, Paper, Checkbox, IconButton } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { BiCaretUp, BiCaretDown } from "react-icons/bi";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";

const useSTyles = makeStyles((theme) => ({
  table: {
    font: "400 11px Roboto, 'Open Sans', sans-serif",
    wordBreak: "keep-all"
  },
  tableRightBorder: {
    borderRight: '1px solid #E0E0E0'
  },
  tableCell: {
    font: "400 11px Roboto, 'Open Sans', sans-serif"
  },
  tableHeader: {
    font: "500 11px Roboto, 'Open Sans', sans-serif",
    minWidth: "96px"
  },
  paddingLeft: {
    paddingLeft: "50px",
  },
  stickyCol: {
    position: "sticky",
    left: 0,
    background: "white",
  },
  stickyHead: {
    position: "sticky",
    top: 0,
    zIndex: "1",
    background: "white",
  },
  overlay: {
    zIndex: "5",
  },
  selectableCol: {
    background: "#f2f2f2",
    padding: "0px 2px",
    display: "flex",
    alignItems: "center"
  },
  smallIconButton: {
    padding: "6px",
  },
}));

const sortingColumns = ['s', 'SUM_Data Received', 'SUM_Data Transceived', 'SUM_Data Transmitted', 'Max_SINR', 'Max_RSRQ', 'Max_Upload', 'Max_Download', 'Max_RSRP', 'd', 'r'];

const IBSTable = ({ columns, height, initData, loadData, selectedRow, setSelectedRow }) => {
  const { operatorsPermittedIBS } = useContext(WebMapContext);

  const classes = useSTyles();
  const tableContainerRef = useRef();
  const container = tableContainerRef.current;

  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const sortDataInGroups = (data, key, direction) => {
    return data.map(group => {
      let sortedOps;
      if (key === 's' || key === 'r' || key === 'd') {
        sortedOps = group.ops.sort((a, b) => {
          if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
          if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
          return 0;
        });
      } else if (sortingColumns.includes(key)) {
        sortedOps = group.ops.sort((a, b) => {
          const aValue = a.m?.find(x => x.ag + "_" + x.a === key)?.v || 0;
          const bValue = b.m?.find(x => x.ag + "_" + x.a === key)?.v || 0;
          if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
          if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
          return 0;
        });
      } else {
        sortedOps = group.ops;
      }
      return {
        ...group,
        ops: sortedOps
      };
    });
  };

  const handleSort = (column) => {
    const sortableColumns = ['s', 'SUM_Data Received', 'SUM_Data Transceived', 'SUM_Data Transmitted', 'Max_SINR', 'Max_RSRQ', 'Max_Upload', 'Max_Download', 'Max_RSRP', 'd', 'r'];
    if (!sortableColumns.includes(column)) {
      return;
    }

    let direction = 'ascending';
    if (sortConfig.key === column && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: column, direction });
  };

  const sortedData = useMemo(() => {
    let processedData = sortConfig.key
      ? sortDataInGroups(data, sortConfig.key, sortConfig.direction)
      : data;
  
    return processedData.map(item => {
      const itemOperators = item.ops.map(op => op.op);

      const hasNonAnonymizedOp = itemOperators.some(op =>
        operatorsPermittedIBS.some(p => p.operator === op && !p.isAnonymized)
      );
  
      return { ...item, isMasked: !hasNonAnonymizedOp };
    });
  }, [data, sortConfig, operatorsPermittedIBS]);


  useEffect(() => {
    if (!Array.isArray(initData)) return;
    const init = [...initData];
    if (selectedRow) {
      const idx = init.findIndex(x => x._id === selectedRow._id && x.lt === selectedRow.lt);
      if (idx > -1) init[idx] = { ...init[idx], selected: true };
    }
    setData(init);
    setPage(2);
    setHasMore(true);
  }, [initData])

  useEffect(() => {
    if (!selectedRow && data.length) { // triggers only when clear all selected btn clicked
      const current = [...data];
      const idx = current.findIndex(x => x.selected);
      if (idx > -1) {
        current[idx] = { ...current[idx], selected: false };
        setData(current);
      }
    }
  }, [selectedRow])

  let attrHeadingsExceptSum = columns.filter((column) => column.type === "attrSubHeader");
  let attrHeadingsForSum = columns.filter((column) => column.type === "attrMainHeader");

  const getOperatorName = (op) => {
    const operator = operatorsPermittedIBS.find(x => x.operator === op);
    return operator?.name;
  }

  const getOperatorLvlVal = (op, fieldTitle) => {
    const obj = op.m?.find(x =>  x.ag+ "_" + x.a === fieldTitle)
    return obj ? obj.v?.toLocaleString('en-US') : "-";
  }

  const handleScroll = useCallback(async () => {
    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 20 &&
      hasMore && page !== 1
    ) {
      const newData = await loadData(page);
      if (newData && newData.length) {
        const combined = [...data, ...newData];
        if (selectedRow) {
          const idx = combined.findIndex(x => x._id === selectedRow._id && x.lt === selectedRow.lt);
          if (idx > -1) combined[idx] = { ...combined[idx], selected: true };
        }
        setData(combined);
        setPage(page + 1);
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  }, [container, hasMore, page, loadData]);

  const onClickRow = (e, idx) => {
    const newRows = [...data];
    if (e.target.checked) { // de-select existing selected row
      const currentIdx = newRows.findIndex(x => x.selected === true);
      if (currentIdx > -1) newRows[currentIdx] = { ...newRows[currentIdx], selected: false };
      setSelectedRow(newRows[idx]);
    } else {
      setSelectedRow();
    }
    newRows[idx] = { ...newRows[idx], selected: e.target.checked };
    setData(newRows);
  }

  const getFormattedCellVal = (val, columnData, isMasked) => {
    if (['Lat', 'Lon'].includes(columnData.label) && isMasked) {
      return val
          ?.toFixed(columnData.round || 5)
          ?.replace(/^\d+/, (match) => 'x'.repeat(match.length))
          ?.replace(/\.\d+/, (match) => `.${'x'.repeat(match.length - 1)}`)
    }
    if (typeof val === 'number') {
      return Number(val.toFixed(columnData.round || 5)).toLocaleString('en-US');
    } else {
      return val || "-";
    }
  };

  const renderSortIcon = (column) => {
    if (!sortingColumns.includes(column)) return null;

    if (sortConfig.key === column) {
      if (sortConfig.direction === 'ascending') {
        return <BiCaretUp style={{ fontSize: "small", color: "gray" }} />;
      } else {
        return <BiCaretDown style={{ fontSize: "small", color: "gray" }} />;
      }
    } else {
      return <BiCaretDown style={{ fontSize: "small", color: "gray" }} />;
    }
  };

  return (
    <TableContainer
      component={Paper}
      style={{
        overflowX: "auto",
        overflowY: "auto",
        height: height - 62,
        width: "100%",
        scrollbarWidth: "thin"
      }}
      ref={tableContainerRef}
      onScroll={handleScroll}
    >
      <Table stickyHeader={true} size="small">
        <TableHead className={`${classes.stickyHead}`}>
          <TableRow>
            {columns.filter((column) => column.type === "mainHeader" || column.type === "attrMainHeader").map((column, i) => {
              if (column.colSpan === 0) {
                return null; // Skip rendering for merged columns
              }
              return (
                <TableCell
                  key={i}
                  className={
                    column.field === "n" ?
                      `${classes.table} ${classes.tableRightBorder} ${classes.tableHeader} ${classes.stickyCol} ${classes.overlay}` :
                      `${classes.table} ${classes.tableRightBorder} ${classes.tableHeader}`
                  }
                  colSpan={column.colSpan || 1}
                  rowSpan={column.rowSpan || 1}
                  align="center"
                  onClick={() => sortingColumns.includes(column.field) && handleSort(column.field)}
                >
                  {column.label}
                  {renderSortIcon(column.field)}
                </TableCell>
              )
            })}
          </TableRow>
          <TableRow>
            {columns.filter((column) => column.type === "subHeader" || column.type === "attrSubHeader").map((column, idx) => {
              if (column.colSpan === 0) {
                return null; // Skip rendering for merged column
              }
              return (
                <TableCell
                  key={idx}
                  className={`${classes.table} ${classes.tableRightBorder} ${classes.tableHeader}`}
                  colSpan={column.colSpan || 1}
                  rowSpan={column.rowSpan || 1}
                  align="center"
                  onClick={() => sortingColumns.includes(column.field) && handleSort(column.field)}
                >
                  {column.label}
                  {renderSortIcon(column.field)}
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>
        {sortedData.map((row, i) => (
          <>
            <TableRow key={i} style={{ backgroundColor: "#f2f2f2" }}> {/* building name */}
              <TableCell
                className={`${classes.table} ${classes.tableRightBorder} ${classes.tableHeader} ${classes.stickyCol} ${classes.selectableCol}`}
                align="left"
              >
                <Checkbox
                  color="primary"
                  checked={row.selected === true}
                  onChange={(e) => onClickRow(e, i)}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 18, paddingRight: "2px" } }}
                  inputProps={{
                    "aria-label": "select all",
                  }}
                />
                {row.n}
              </TableCell>
              {columns.filter(column => column.type === "attrMainHeader" || column.type === "attrSubHeader" || column.type === "subHeader").map((col) =>
                (<TableCell className={`${classes.table} ${classes.tableRightBorder} ${classes.tableHeader}`} style={{ width: "130px" }}></TableCell>
              ))}
              <TableCell className={`${classes.table} ${classes.tableRightBorder} ${classes.tableHeader}`} style={{ width: "130px", backgroundColor: "#f2f2f2" }}></TableCell>
              <TableCell className={`${classes.table} ${classes.tableRightBorder} ${classes.tableHeader}`} style={{ width: "130px", backgroundColor: "#f2f2f2" }}></TableCell>
            </TableRow>
            {row.ops.map((item, index) => (
              <TableRow key={index} >
                {/* operator names */}
                <TableCell
                  className={`${classes.table} ${classes.tableRightBorder} ${classes.tableCell} ${classes.stickyCol} ${classes.paddingLeft}`}
                  align="right"
                >
                  {getOperatorName(item.op)}
                </TableCell>
                {/* rsrp_max, rsrq_max,... */}
                {attrHeadingsExceptSum.map((col, idx) => (
                  <TableCell
                    key={idx}
                    className={`${classes.table} ${classes.tableRightBorder} ${classes.tableCell}`}
                    align="center"
                  >
                    {getOperatorLvlVal(item, col.field)}
                  </TableCell>
                ))}
                {/* sum_Data Received, sum_data,... */}
                {attrHeadingsForSum.map((col, idx) => (
                  <TableCell
                    key={idx}
                    className={`${classes.table} ${classes.tableRightBorder} ${classes.tableCell}`}
                    align="center"
                  >
                    {getOperatorLvlVal(item, col.field)}
                  </TableCell>
                ))}
                {/* ibs score, tot_d, tot_samples */}
                {columns.filter(column =>  ["s", "d", "r"].includes(column.field) || (column.type === "subHeader" && column.field !== "op")).map((col, idx) => (
                  index === 0 || (index !== 0 && !col.common)
                    ? <TableCell
                      key={idx}
                      className={`${classes.table} ${classes.tableRightBorder} ${classes.tableCell}`}
                      align="center"
                      rowSpan={col.common ? row.ops.length : 1}
                    >
                      {getFormattedCellVal(item[col.field] ? item[col.field] : row[col.field], col, row?.isMasked)}
                    </TableCell>
                    : <></>
                ))}
              </TableRow>
            ))}
          </>
        ))}
      </Table>
    </TableContainer>
  )

}

export default memo(IBSTable);
